import { useState } from "react";
import { ChevronLeft } from "lucide-react";

import { Button } from "@/components/ui/button";
import { SetupMode } from "@/types/mockups/genie-setup-v2";

import { GenieSetupDrawer } from "./GenieSetupDrawer";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";

const GENIE_AVATAR = "/genie-mascots/role/concierge.png";

const GenieSetupV2MockupPage = () => {
  const [drawerMode, setDrawerMode] = useState<SetupMode | null>(null);

  const handleSelect = (mode: SetupMode) => {
    setDrawerMode(mode);
  };

  return (
    <div className="h-screen w-full bg-[#F9FAFC] flex flex-col">
      {/* Top bar — matches existing SetupModeSelector */}
      <div className="px-4 pt-3 md:px-6 md:pt-4">
        <Button
          onClick={() => window.history.back()}
          variant="ghost"
          size="sm"
          className="text-gray-600 px-2 md:px-3 py-1 md:py-2 text-sm md:text-base min-w-0 h-8 md:h-9 flex items-center gap-1 md:gap-2 transition-colors duration-200 rounded-md hover:text-white hover:bg-gradient-to-r hover:from-[#4E9CFF] hover:to-[#FF4DB8]"
        >
          <ChevronLeft size={16} />
          <span className="hidden md:inline">Exit</span>
        </Button>
      </div>

      {/* Centered content */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 pb-12 overflow-y-auto">
        <img
          src={GENIE_AVATAR}
          alt="Help Genie"
          className="w-28 h-28 md:w-36 md:h-36 rounded-full object-cover mb-6 ring-2 ring-primary/10"
        />

        <h1 className="text-xl md:text-2xl font-semibold text-gray-800 text-center mb-2">
          How would you like to set up your Genie?
        </h1>
        <p className="text-sm md:text-base text-gray-500 text-center mb-8 md:mb-10 max-w-md">
          Choose your preferred way to create a new genie
        </p>

        {/* Three mode cards — Talk + Type stay, Click is new */}
        <div className="flex flex-col sm:flex-row gap-5 w-full max-w-4xl">
          <ModeCard
            genieIcon="microphone"
            title="Talk to Set Up"
            description="Speak with our setup guide — we'll build your genie as you talk."
            onClick={() => handleSelect("voice")}
          />
          <ModeCard
            genieIcon="chat"
            title="Type to Set Up"
            description="Chat your way through the setup — describe what you need and we'll build it."
            onClick={() => handleSelect("type")}
          />
          <ModeCard
            genieIcon="clipboard-check"
            title="Click to Set Up"
            description="Answer a few quick questions and pick from smart options — no writing needed."
            badge="New"
            onClick={() => handleSelect("click")}
          />
        </div>

        {/* Upload Image option */}
        <button
          onClick={() => handleSelect("click")}
          className="mt-8 md:mt-10 flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          <GenieIcon name="eye" size={20} className="w-5 h-5 opacity-60" />
          Upload Image Instead
        </button>
      </div>

      <GenieSetupDrawer
        isOpen={drawerMode !== null}
        initialMode={drawerMode}
        onClose={() => setDrawerMode(null)}
      />
    </div>
  );
};

const ModeCard = ({
  genieIcon,
  title,
  description,
  onClick,
  badge,
}: {
  genieIcon: GenieIconName;
  title: string;
  description: string;
  onClick: () => void;
  badge?: string;
}) => (
  <button
    onClick={onClick}
    className="flex-1 group relative rounded-xl p-[1.5px] bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] transition-all duration-300 hover:shadow-lg hover:shadow-primary/20 hover:scale-[1.02]"
  >
    <div className="bg-white rounded-[10.5px] p-6 md:p-8 flex flex-col items-center text-center h-full">
      {badge && (
        <span className="absolute top-3 right-3 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-genie-secondary/10 text-genie-secondary">
          {badge}
        </span>
      )}
      <GenieIcon name={genieIcon} size={56} className="w-14 h-14 md:w-16 md:h-16 mb-4" />
      <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-1.5">{title}</h3>
      <p className="text-xs md:text-sm text-gray-500 leading-relaxed">{description}</p>
    </div>
  </button>
);

export default GenieSetupV2MockupPage;
